<script setup lang="ts">
import FacebookOutlineIcon from "@/assets/icons/social-outline/facebook.svg?component";
import LinkedinOutlineIcon from "@/assets/icons/social-outline/linkedin.svg?component";
import WhatsappOutlineIcon from "@/assets/icons/social-outline/whatsapp.svg?component";
import XOutlineIcon from "@/assets/icons/social-outline/x.svg?component";
import { useProductNoun } from "@/composition/useProductNoun";
import { useAppStore } from "@/store/app";
import { getSocialShareUrl } from "@/util/shareLinks";
import {
  Copy01Icon,
  LinkExternal01Icon,
  Mail01Icon,
  QrCode01Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIInput } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import FederatedQrIntegrationPanel from "./FederatedQrIntegrationPanel.vue";

const props = withDefaults(
  defineProps<{
    url: string;
    compact?: boolean;
  }>(),
  { compact: false }
);
const emit = defineEmits<{
  (event: "open-link"): void;
  (event: "open-email"): void;
  (event: "open-qr"): void;
}>();

const store = useAppStore();
const { t } = useI18n();
const { assetParams } = useProductNoun();

const socialLinks = computed(() => [
  {
    platform: "facebook" as const,
    icon: FacebookOutlineIcon,
    label: t("integrate.share.social.facebook"),
  },
  {
    platform: "whatsapp" as const,
    icon: WhatsappOutlineIcon,
    label: t("integrate.share.social.whatsapp"),
  },
  {
    platform: "linkedin" as const,
    icon: LinkedinOutlineIcon,
    label: t("integrate.share.social.linkedin"),
  },
  {
    platform: "x" as const,
    icon: XOutlineIcon,
    label: t("integrate.share.social.x"),
  },
]);

const copyLink = () => store.copyToClipBoard(props.url);
</script>

<template>
  <aside
    class="share-panel"
    :class="{
      'share-panel--compact': compact,
      'share-panel--scrollable': !compact,
    }"
  >
    <section class="share-panel__section" aria-labelledby="share-link-heading">
      <div v-if="!compact" class="share-panel__section-copy">
        <h3 id="share-link-heading">
          {{ t("integrate.share.copyLinkTitle", assetParams) }}
        </h3>
        <p>{{ t("integrate.share.copyLinkDescription", assetParams) }}</p>
      </div>
      <div class="share-panel__link-controls">
        <UIInput
          :id="compact ? 'integrate-share-url-compact' : 'integrate-share-url'"
          :model-value="url"
          :readonly="true"
          :input-props="{
            'data-testid': 'share-url-input',
            'aria-label': t('integrate.share.copyLink'),
            class: 'share-panel__url-input',
            style: {
              padding: '0 12px',
              textOverflow: 'ellipsis',
            },
          }"
        />
        <div class="share-panel__actions">
          <a
            data-testid="share-open-link"
            class="share-panel__icon-button"
            :href="url"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="t('integrate.share.openLink')"
            @click="emit('open-link')"
          >
            <LinkExternal01Icon class="share-panel__button-icon" />
          </a>
          <UIButton
            id="share-copy-link"
            data-testid="share-copy-link"
            class="share-panel__copy-button"
            type="primary"
            :size="compact ? 'medium' : 'small'"
            @click="copyLink"
          >
            <Copy01Icon class="share-panel__button-icon" />
            <span v-if="!compact">
              {{ t("integrate.share.copyLinkButton") }}
            </span>
            <span v-if="compact" class="sr-only">
              {{ t("integrate.share.copyLinkButton") }}
            </span>
          </UIButton>
        </div>
      </div>
    </section>

    <section
      class="share-panel__section"
      aria-labelledby="share-social-heading"
    >
      <div v-if="!compact" class="share-panel__section-copy">
        <h3 id="share-social-heading">
          {{ t("integrate.share.shareOnSocialMedia") }}
        </h3>
        <p>{{ t("integrate.share.socialDescription", assetParams) }}</p>
      </div>
      <div class="share-panel__socials">
        <UIButton
          v-if="compact && store.formId"
          data-testid="share-social-email"
          text
          class="share-panel__social-link"
          :aria-label="t('common.email')"
          @click="emit('open-email')"
        >
          <Mail01Icon />
        </UIButton>
        <a
          v-for="social in socialLinks"
          :key="social.platform"
          :data-testid="`share-social-${social.platform}`"
          class="share-panel__social-link"
          :href="getSocialShareUrl(social.platform, encodeURIComponent(url))"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="social.label"
          @click="emit('open-link')"
        >
          <component
            :is="social.icon"
            aria-hidden="true"
            class="share-panel__social-icon"
          />
        </a>
        <UIButton
          v-if="compact"
          id="share-qr-code"
          data-testid="share-social-qr"
          text
          class="share-panel__social-link"
          :aria-label="t('integrate.share.qrCode')"
          @click="emit('open-qr')"
        >
          <QrCode01Icon />
        </UIButton>
      </div>
    </section>

    <section
      v-if="!compact"
      data-testid="share-qr-slot"
      class="share-panel__section"
      aria-labelledby="share-qr-heading"
    >
      <div class="share-panel__section-copy">
        <h3 id="share-qr-heading">
          {{ t("integrate.share.shareQr") }}
        </h3>
        <p>{{ t("integrate.share.shareQrDescription", assetParams) }}</p>
      </div>
      <FederatedQrIntegrationPanel :url="url" />
    </section>
  </aside>
</template>

<style scoped>
.share-panel {
  display: flex;
  width: var(--integrate-side-panel-width);
  min-width: var(--integrate-side-panel-min-width);
  max-width: var(--integrate-side-panel-max-width);
  flex: 0 0 auto;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  border-radius: 12px;
  background: var(--base-white, #fff);
  box-sizing: border-box;
}
.share-panel--scrollable {
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
}
.share-panel__section h3,
.share-panel__section-copy h3 {
  margin: 0;
  color: var(--gray-900, #101828);
  font-weight: 600;
}
.share-panel__section-copy p {
  margin: 6px 0 0;
  color: var(--gray-600, #475467);
  font-size: 14px;
  line-height: 20px;
}
.share-panel__section {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 16px;
}
.share-panel__section + .share-panel__section {
  padding-top: 24px;
  border-top: 1px solid var(--gray-200, #eaecf0);
}
.share-panel__section h3 {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}
.share-panel__link-controls {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}
.share-panel__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}
.share-panel__link-controls :deep(.hl-input-text) {
  min-width: 0;
  width: 100%;
  flex: 1;
}
.share-panel__link-controls :deep(.n-input-wrapper) {
  padding: 0 !important;
}
.share-panel__link-controls :deep(.n-input__suffix) {
  display: none;
}
.share-panel__link-controls :deep(.share-panel__url-input) {
  min-width: 0;
  overflow: hidden;
  padding: 0 12px !important;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.share-panel__button-icon {
  width: 16px;
  height: 16px;
}
.share-panel__icon-button {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  border: 1px solid var(--gray-300, #d0d5dd);
  border-radius: 8px;
  color: var(--gray-700, #344054);
  background: #fff;
}
.share-panel__icon-button:focus-visible,
.share-panel__social-link:focus-visible {
  outline: 2px solid var(--primary-600, #155eef);
  outline-offset: 2px;
}
.share-panel__socials {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.share-panel__social-link {
  display: flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: var(--gray-100, #f2f4f7);
}
.share-panel__social-link svg {
  color: var(--gray-700, #344054);
}
.share-panel__social-link svg:not(.share-panel__social-icon) {
  width: 20px;
  height: 20px;
}
.share-panel__copy-button :deep(.n-button__content) {
  gap: 8px;
}
.share-panel__social-link:is(button) {
  padding: 0;
  color: var(--gray-700, #344054);
  cursor: pointer;
}
.share-panel--compact {
  width: 100%;
  min-width: 0;
  max-width: none;
  flex-basis: auto;
  gap: 12px;
  padding: 0;
}
.share-panel--compact .share-panel__section + .share-panel__section {
  padding-top: 0;
  border-top: 0;
}
.share-panel--compact .share-panel__link-controls {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
.share-panel--compact .share-panel__link-controls :deep(.hl-input-text),
.share-panel--compact .share-panel__link-controls :deep(.n-input) {
  min-width: 0;
  height: 40px;
  flex: 1;
}
.share-panel--compact .share-panel__link-controls :deep(input) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.share-panel--compact .share-panel__actions {
  flex: 0 0 auto;
  gap: 8px;
}
.share-panel--compact .share-panel__icon-button,
.share-panel--compact .share-panel__copy-button {
  width: 40px;
  min-width: 40px;
  height: 40px;
  padding: 0 !important;
  border-radius: 8px;
}
.share-panel--compact .share-panel__button-icon {
  width: 20px;
  height: 20px;
}
.share-panel--compact .share-panel__copy-button :deep(.n-button__content) {
  gap: 6px;
}
@media (max-width: 1024px) {
  .share-panel {
    width: 100%;
    min-width: 0;
    max-width: none;
    flex-basis: auto;
  }
}
</style>
